import React from 'react'

import {
  Body2,
  Bold,
  Box,
  Display,
  Heading1,
  Heading2,
  JustifyContent,
  Link,
  LinkVariant,
  Space,
  TextAlign,
} from '@gousto-internal/citrus-react'

import { CtaLink, mapCmsCta } from '../../cta'
import type { OnTriggerPromoCode } from '../../cta'
import type { VideoContentSectionFields } from './types'

export type VideoContentSectionProps = VideoContentSectionFields & {
  onTriggerPromocode?: OnTriggerPromoCode
}

type RteNode = {
  type?: string
  uid?: string
  text?: string
  children?: RteNode[]
  attrs?: {
    href?: string
    url?: string
  }
  bold?: boolean
  italic?: boolean
  underline?: boolean
}

function isLeaf(node: RteNode): node is RteNode & { text: string } {
  return typeof node.text === 'string'
}

function RichTextLeaf({ node }: { node: RteNode & { text: string } }) {
  let content: React.ReactNode = node.text
  if (node.bold) {
    content = <Bold>{content}</Bold>
  }
  if (node.italic) {
    content = <em>{content}</em>
  }
  if (node.underline) {
    content = <u>{content}</u>
  }
  return <>{content}</>
}

function RichTextChildren({ nodes }: { nodes: RteNode[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        const key = node.uid ?? `node-${index}`

        if (isLeaf(node)) {
          return <RichTextLeaf key={key} node={node} />
        }

        const linkHref = node.attrs?.href ?? node.attrs?.url

        if (node.type === 'a' && linkHref) {
          return (
            <Link key={key} variant={LinkVariant.None} href={linkHref}>
              <RichTextChildren nodes={node.children ?? []} />
            </Link>
          )
        }

        return <RichTextChildren key={key} nodes={node.children ?? []} />
      })}
    </>
  )
}

function VideoContentBody({ body }: { body?: VideoContentSectionFields['body'] }) {
  if (!body) {
    return null
  }

  const root = body as RteNode
  const paragraphs = (root.children ?? []).filter(
    (block) => block.type === 'p' || (block.children?.length ?? 0) > 0,
  )

  if (paragraphs.length === 0) {
    return null
  }

  return (
    <Box paddingBottom={6}>
      {paragraphs.map((paragraph, index) => (
        <React.Fragment key={paragraph.uid ?? `p-${index}`}>
          {index > 0 && <Space size={4} />}
          <Body2 size={4} textAlign={TextAlign.Center} style={{ whiteSpace: 'pre-wrap' }}>
            <RichTextChildren nodes={paragraph.children ?? []} />
          </Body2>
        </React.Fragment>
      ))}
    </Box>
  )
}

function SectionHeading({
  level,
  children,
}: {
  level: VideoContentSectionFields['heading_level']
  children: React.ReactNode
}) {
  if (level === 'h1') {
    return <Heading1 textAlign={TextAlign.Center}>{children}</Heading1>
  }

  return <Heading2 textAlign={TextAlign.Center}>{children}</Heading2>
}

export function VideoContentSection({
  heading,
  heading_level,
  body,
  video,
  poster_image,
  captions,
  cta,
  onTriggerPromocode,
}: VideoContentSectionProps) {
  const isValidVideo = video.content_type.startsWith('video/')
  const posterUrl = poster_image?.content_type.startsWith('image/') ? poster_image.url : undefined
  const hasValidCaptions =
    captions.content_type === 'text/vtt' || captions.filename.endsWith('.vtt')
  const shouldRenderVideo = isValidVideo && hasValidCaptions
  const ctaLink = mapCmsCta(cta, onTriggerPromocode)

  return (
    <Box paddingV={[8, 12, 12, 16]} paddingH={[4, 6, 14]}>
      <Box maxWidth="960px" style={{ margin: '0 auto' }}>
        <Box maxWidth="640px" style={{ margin: '0 auto' }}>
          <SectionHeading level={heading_level}>{heading}</SectionHeading>
          <Space size={4} />
          <VideoContentBody body={body} />
        </Box>

        {shouldRenderVideo && (
          <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
            <Box
              width="100%"
              style={{
                aspectRatio: '16 / 9',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <video
                src={video.url}
                poster={posterUrl}
                controls
                playsInline
                preload="metadata"
                style={{
                  display: 'block',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              >
                <track kind="captions" src={captions.url} srcLang="en" label="English captions" />
              </video>
            </Box>
          </Box>
        )}

        {ctaLink && (
          <>
            <Space size={6} />
            <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
              <CtaLink cta={ctaLink} />
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
